import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { PricingCard } from './PricingCard';
import type { PricingColumnConfig, PricingTeaserResolved } from './types';
import { Button } from '@/components/Button';
import { Carousel } from '@/components/Carousel';
import { Section } from '@/components/Section';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { hasRichTextContent } from '@/lib/render/hasRichTextContent';
import { useSubstitute } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';
import { Text } from '@/lib/render/Text';

/**
 * Mobile: rotate so the featured column is first.
 * Desktop order: A B C. Mobile order: B A C (if B is featured).
 */
function rotateToFeatured(
  columns: PricingColumnConfig[],
): PricingColumnConfig[] {
  const index = columns.findIndex((c) => c.featured);
  if (index <= 0) {
    return columns;
  }
  return [...columns.slice(index), ...columns.slice(0, index)];
}

export function PricingTeaserComponent({
  resolved,
  campaignPricing,
}: {
  resolved: PricingTeaserResolved;
  /**
   * Campaign variant B's price typography. Set by `PricingTeaserComponentB`;
   * variants A and C leave it unset and keep the styling they had before
   * SH-415.
   */
  campaignPricing?: boolean;
}) {
  const t = useTranslations('pricingTeaser');
  const sub = useSubstitute();
  const columns = resolved.columns ?? [];

  // The backend says *whether* the partner hid the table; what stands in for it
  // is decided here, because the standard wording is per-locale. An editor who
  // clicks into the richtext field and leaves it stores an empty paragraph, so
  // "authored something" has to be `hasRichTextContent`, not a truthiness test.
  const showHiddenPricingText = resolved.shouldShowHiddenPricingText === true;
  const authoredHiddenPricingText = hasRichTextContent(
    resolved.hiddenPricingText,
  )
    ? resolved.hiddenPricingText
    : undefined;

  if (columns.length === 0 && !resolved.ctaLabel && !showHiddenPricingText) {
    return null;
  }

  const mobileColumns = rotateToFeatured(columns);

  // A part's `pt-8` separates it from the part before it, so the first part
  // that renders gets none — the root's top padding already spaces it from the
  // widget above. Without this, a table hidden by the partner left the
  // stand-in text 80px below its heading (SH-485).
  const hasColumns = columns.length > 0;
  const beforeFootnote = hasColumns || showHiddenPricingText;
  const beforeBenefits = beforeFootnote || !!resolved.footnote;
  const beforeCta = beforeBenefits || !!resolved.benefits;

  // Grid classes: center fewer than 3 columns
  const gridCols =
    columns.length >= 3
      ? 'md:grid-cols-3'
      : columns.length === 2
        ? 'md:grid-cols-2 md:max-w-4xl md:mx-auto'
        : 'md:grid-cols-1 md:max-w-sm md:mx-auto';

  // Inside the widget, spacing is top padding on the part that needs the gap,
  // never bottom margin on the part before it — the same rule the widget roots
  // follow (see 70.12-Widget-Spacing). Every part here is optional, so a
  // trailing `mb-*` left a gap under whichever happened to render last, and
  // margins between two parts collapsed instead of adding.
  return (
    // `pt-8` overrides the shared `<Section>`'s `pt-4` (twMerge keeps the
    // caller's class). Exception to the `pt-4` default: each pricing card
    // hangs a badge above its own top edge, which would collide with the
    // widget above at the standard spacing. No cards, no badge, no exception.
    <Section className={hasColumns ? 'pt-8' : undefined}>
      {hasColumns && (
        <>
          <div className={classNames('hidden gap-6 md:grid', gridCols)}>
            {columns.map((col, i) => (
              <PricingCard
                key={`${col.planHeader}-${i}`}
                col={col}
                campaignPricing={campaignPricing}
              />
            ))}
          </div>

          <Carousel
            className="md:hidden"
            viewportClassName="pt-4"
            prevLabel="Vorheriger Tarif"
            nextLabel="Nächster Tarif"
            dotLabels={mobileColumns.map((_, i) => `Gehe zu Tarif ${i + 1}`)}
            slides={mobileColumns.map((col, i) => (
              <PricingCard
                key={`${col.planHeader}-${i}`}
                col={col}
                campaignPricing={campaignPricing}
              />
            ))}
          />
        </>
      )}

      {showHiddenPricingText && (
        <div className={classNames('text-center', hasColumns && 'pt-8')}>
          {authoredHiddenPricingText ? (
            <RichText nodes={authoredHiddenPricingText} />
          ) : (
            <p>{t('hiddenPricingFallback')}</p>
          )}
        </div>
      )}

      {resolved.footnote && (
        <Text
          as="p"
          field="footnote"
          className={classNames(
            'text-center text-base',
            beforeFootnote && 'pt-8',
          )}
        >
          {resolved.footnote}
        </Text>
      )}

      {resolved.benefits && (
        <div
          className={classNames(
            'benefits-checkmarks',
            beforeBenefits && 'pt-8',
          )}
        >
          <RichText nodes={resolved.benefits} />
        </div>
      )}

      {resolved.ctaLabel && (
        <div className={classNames('text-center', beforeCta && 'pt-8')}>
          <Button
            href={substituteHref(resolved.ctaUrl, sub) || '#beratungstermin'}
            size="lg"
            id="pricing-teaser-cta"
            onClick={() =>
              pushCmsEventTracking(resolved.tracking, {
                label: resolved.ctaLabel,
              })
            }
            className="w-full py-3 text-sm md:w-auto md:!py-3 md:!text-lg/5 md:text-sm/4"
          >
            {resolved.ctaLabel}
          </Button>
        </div>
      )}
    </Section>
  );
}
